import { Injectable } from '@angular/core';
import {
  ActionDeletionImpact,
  ApiWorkOrderActionsAdapterService,
} from '@fullyops/data/work-order-actions/api-adapter.service';
import {
  WorkOrderAction,
  WorkOrderActionInput,
} from '@fullyops/data/work-order-actions/work-order-action';
import { createStore } from '@ngneat/elf';
import {
  deleteAllEntities,
  deleteEntities,
  selectAllEntities,
  setEntities,
  withEntities,
} from '@ngneat/elf-entities';
import { forkJoin, Observable, Subject } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class WorkOrderActionsService {
  private store = createStore(
    { name: 'work-order-actions' },
    withEntities<WorkOrderAction>({ idKey: 'id' }),
  );

  constructor(private api: ApiWorkOrderActionsAdapterService) {}

  actions$ = this.store.pipe(selectAllEntities());

  preload(actions: WorkOrderAction[]) {
    this.store.update(setEntities(actions));
  }

  private refreshByWorkOrderId(id: string) {
    this.api.getAll(id).subscribe((res) => this.store.update(setEntities(res)));
  }

  // These methods return an Observable so that the work order actions form can
  // perform some side effects when these operations are complete.

  create(
    workOrderId: string,
    action: WorkOrderActionInput,
    attachments: File[],
  ): Observable<void> {
    // Use an intermediate Subject so that we can subscribe to the request
    // internally while also letting the caller do so.
    const sub = new Subject<void>();
    this.api.create(workOrderId, action).subscribe({
      next: (actionId) => {
        if (attachments.length === 0) {
          sub.next();
          return;
        }
        this.api.attach(workOrderId, actionId, attachments).subscribe(sub);
      },
      // Forwarded so that the caller can react to a rejected action, such as
      // one whose equipment already belongs to another company.
      error: (err) => sub.error(err),
    });
    this.refreshWhenSaved(sub, workOrderId);
    return sub;
  }

  update(
    workOrderId: string,
    actionId: string,
    action: WorkOrderActionInput,
    attachments: File[],
  ): Observable<void> {
    const sub = new Subject<void>();
    if (attachments.length === 0) {
      this.api.update(workOrderId, actionId, action).subscribe(sub);
    } else {
      forkJoin([
        this.api.update(workOrderId, actionId, action),
        this.api.attach(workOrderId, actionId, attachments),
      ]).subscribe({
        next: () => sub.next(),
        error: (err) => sub.error(err),
      });
    }
    this.refreshWhenSaved(sub, workOrderId);
    return sub;
  }

  // The internal subscription needs an error handler of its own, since a failed
  // save is reported to the caller and must not also surface as an unhandled
  // error here.
  private refreshWhenSaved(sub: Subject<void>, workOrderId: string) {
    sub.subscribe({
      next: () => this.refreshByWorkOrderId(workOrderId),
      error: () => {},
    });
  }

  getDeletionImpact(
    workOrderId: string,
    actionId: string,
  ): Promise<ActionDeletionImpact> {
    return new Promise((resolve, reject) =>
      this.api
        .getDeletionImpact(workOrderId, actionId)
        .subscribe({ next: resolve, error: reject }),
    );
  }

  delete(workOrderId: string, actionId: string) {
    const sub = new Subject<void>();
    this.api.delete(workOrderId, actionId).subscribe(sub);
    sub.subscribe(() => this.store.update(deleteEntities(actionId)));
    return sub;
  }

  clear() {
    this.store.update(deleteAllEntities());
  }
}
